import React, {useState} from 'react'
import {motion} from 'framer-motion'
import {useFamily} from '../../contexts/FamilyContext'
import {useAuth} from '../../contexts/AuthContext'
import {useCelebrationContext} from '../../contexts/CelebrationContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import Card from '../ui/Card'
import TaskCompletionModal from './TaskCompletionModal'

const {FiCheck, FiClock, FiStar, FiRepeat, FiUser, FiUsers, FiCalendar, FiEdit3, FiTrash2, FiMoreVertical, FiTimer, FiZap} = FiIcons

const TaskCard = ({task, child, view = 'parent', onEdit, onDelete}) => {
  const {completeTask} = useFamily()
  const {family} = useAuth()
  const {celebrateTaskComplete} = useCelebrationContext()
  const [showActions, setShowActions] = useState(false)
  const [showCompletionModal, setShowCompletionModal] = useState(false)

  const gamificationEnabled = family?.gamification_settings?.enhanced_points?.enabled || 
                             family?.gamification_settings?.point_multipliers?.enabled

  const handleComplete = async () => {
    if (gamificationEnabled) {
      setShowCompletionModal(true)
    } else {
      // Standard completion
      if (child) {
        const result = await completeTask(task.id, child.id)
        if (result && !result.error) {
          celebrateTaskComplete(child.avatar || '👦', task.title, task.points)
        }
      }
    }
  }

  const getTaskIcon = (category) => {
    const icons = {
      'cleaning': '🧹',
      'dishes': '🍽️',
      'laundry': '👕',
      'pets': '🐕',
      'homework': '📚',
      'outdoor': '🌱',
      'cooking': '👨‍🍳',
      'organization': '📦',
      'other': '⭐'
    }
    return icons[category] || '⭐'
  }

  const getDifficultyColor = (points) => {
    if (points >= 30) return 'text-red-500 dark:text-red-400'
    if (points >= 20) return 'text-orange-500 dark:text-orange-400'
    if (points >= 10) return 'text-yellow-500 dark:text-yellow-400'
    return 'text-green-500 dark:text-green-400'
  }

  const getRecurringBadge = () => {
    if (!task.recurring_pattern || task.recurring_pattern === 'once') {
      return null
    }

    const patterns = {
      'daily': { label: 'Daily', color: 'success', icon: FiRepeat },
      'weekly': { label: 'Weekly', color: 'info', icon: FiCalendar },
      'monthly': { label: 'Monthly', color: 'secondary', icon: FiCalendar }
    }

    const pattern = patterns[task.recurring_pattern]
    if (!pattern) return null

    return (
      <Badge variant={pattern.color} size="sm" className="flex items-center gap-1">
        <SafeIcon icon={pattern.icon} className="text-xs" />
        {pattern.label}
      </Badge>
    )
  }

  const getAssignmentInfo = () => {
    if (!task.assigned_to) {
      return {
        text: 'Available to all',
        icon: FiUsers,
        color: 'text-blue-600 dark:text-blue-400'
      }
    }

    const assignedChild = family?.children?.find(c => c.id === task.assigned_to)
    return {
      text: assignedChild ? `Assigned to ${assignedChild.name}` : 'Assigned',
      icon: FiUser,
      color: 'text-purple-600 dark:text-purple-400',
      avatar: assignedChild?.avatar
    }
  }

  const getWeeklyDaysText = () => {
    if (task.recurring_pattern !== 'weekly' || !task.recurring_days) {
      return null
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const selectedDays = task.recurring_days
      .sort()
      .map(dayIndex => dayNames[dayIndex])
      .join(', ')
    
    return selectedDays
  }

  const formatTimeDisplay = (minutes) => {
    if (!minutes) return null
    
    if (minutes < 60) {
      return `${minutes} min`
    }
    
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  }

  const getTimeBadgeColor = (minutes) => {
    if (minutes <= 10) return 'success'
    if (minutes <= 30) return 'warning'
    return 'secondary'
  }

  const getCurrentBonuses = () => {
    if (!gamificationEnabled) return []
    
    const bonuses = []
    const now = new Date()
    const currentTime = now.toTimeString().slice(0, 5)
    const currentDay = now.getDay()
    
    // Check Happy Hour
    const happyHour = family?.gamification_settings?.point_multipliers?.happy_hour
    if (happyHour?.enabled && 
        happyHour.days?.includes(currentDay) &&
        currentTime >= happyHour.start_time && 
        currentTime <= happyHour.end_time) {
      bonuses.push({ text: 'Happy Hour', multiplier: happyHour.multiplier })
    }
    
    // Check Weekend Warriors
    const weekendWarriors = family?.gamification_settings?.point_multipliers?.weekend_warriors
    if (weekendWarriors?.enabled && 
        [0, 6].includes(currentDay) &&
        weekendWarriors.categories?.includes(task.category)) {
      bonuses.push({ text: 'Weekend Warrior', multiplier: weekendWarriors.multiplier })
    }
    
    // Check Challenge Week
    const challengeWeek = family?.gamification_settings?.point_multipliers?.challenge_week
    if (challengeWeek?.enabled && challengeWeek.start_date && challengeWeek.end_date) {
      const today = now.toISOString().split('T')[0]
      if (today >= challengeWeek.start_date && today <= challengeWeek.end_date) {
        bonuses.push({ text: 'Challenge Week', multiplier: challengeWeek.multiplier })
      }
    }
    
    return bonuses
  }

  const assignment = getAssignmentInfo()
  const currentBonuses = getCurrentBonuses()

  return (
    <>
      <Card className="p-4 hover:shadow-xl transition-all duration-300 relative">
        {/* Task Actions Menu - Parent View Only */}
        {view === 'parent' && (
          <div className="absolute top-4 right-4">
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowActions(!showActions)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <SafeIcon icon={FiMoreVertical} className="text-gray-500 dark:text-gray-400" />
              </Button>

              {showActions && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-10 min-w-[120px]"
                >
                  <button
                    onClick={() => {
                      onEdit?.(task)
                      setShowActions(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiEdit3} className="text-xs text-blue-500 dark:text-blue-400" />
                    Edit Task
                  </button>
                  <button
                    onClick={() => {
                      onDelete?.(task)
                      setShowActions(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiTrash2} className="text-xs text-red-500 dark:text-red-400" />
                    Delete Task
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1 mr-12">
            <div className="text-2xl">
              {getTaskIcon(task.category)}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                {task.title}
              </h3>
              {task.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                  {task.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="primary" className="flex items-center gap-1">
              <SafeIcon icon={FiStar} className={`text-sm ${getDifficultyColor(task.points)}`} />
              <span>{task.points} pts</span>
            </Badge>
            {gamificationEnabled && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <SafeIcon icon={FiZap} className="text-xs text-primary-500" />
                <span>Enhanced</span>
              </Badge>
            )}
          </div>
        </div>

        {/* Active Bonuses */}
        {currentBonuses.length > 0 && (
          <div className="mb-3 p-2 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2 mb-1">
              <SafeIcon icon={FiZap} className="text-yellow-600 dark:text-yellow-400 text-sm" />
              <span className="text-xs font-medium text-yellow-800 dark:text-yellow-200">Active Bonuses:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {currentBonuses.map((bonus, index) => (
                <Badge key={index} variant="warning" size="sm">
                  {bonus.text} ({bonus.multiplier}x)
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Time Estimate */}
        {task.estimated_time_minutes && (
          <div className="mb-3">
            <Badge variant={getTimeBadgeColor(task.estimated_time_minutes)} className="flex items-center gap-1 w-fit">
              <SafeIcon icon={FiTimer} className="text-xs" />
              <span>{formatTimeDisplay(task.estimated_time_minutes)}</span>
            </Badge>
          </div>
        )}

        {/* Assignment and Recurring Info */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* Assignment Badge */}
          <div className="flex items-center gap-1 text-sm">
            {assignment.avatar ? (
              <span className="text-lg">{assignment.avatar}</span>
            ) : (
              <SafeIcon icon={assignment.icon} className={`text-xs ${assignment.color}`} />
            )}
            <span className={`text-xs ${assignment.color}`}>{assignment.text}</span>
          </div>

          {/* Recurring Badge */}
          {getRecurringBadge()}
        </div>

        {/* Weekly Days Display */}
        {getWeeklyDaysText() && (
          <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">
              📅 Repeats: {getWeeklyDaysText()}
            </div>
          </div>
        )}

        {/* Action Area */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            {task.start_date && (
              <div className="flex items-center gap-1">
                <SafeIcon icon={FiCalendar} className="text-xs" />
                <span>Starts {new Date(task.start_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {view === 'child' && child && (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="success"
                size="sm"
                onClick={handleComplete}
                className="flex items-center gap-2"
              >
                <SafeIcon icon={FiCheck} className="text-sm" />
                {gamificationEnabled ? 'Complete+' : 'Complete'}
              </Button>
            </motion.div>
          )}
        </div>

        {/* Due Date or End Date */}
        {(task.due_date || task.end_date) && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <SafeIcon icon={FiClock} className="text-xs" />
              {task.due_date && (
                <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
              )}
              {task.end_date && (
                <span>Ends: {new Date(task.end_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Task Completion Modal */}
      <TaskCompletionModal
        task={task}
        child={child}
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
      />
    </>
  )
}

export default TaskCard